import type { Catalogs, Translation, Translate } from "@amtana/i18n";
import type { MemberView, MembershipRole } from "@amtana/organization-contract";

// Pure copy and derivations for the members directory (S3.02, S4.06) and the
// accept-invite screen (S3.04, S3.05, S3.06): no React, no I/O, so this
// tests without a browser. The members/invitations screens' copy (S6.05):
// one key per string, in English and Spanish, read through `useOrganizationCopy`
// (behavior/use-organization-copy.ts). A key with no Spanish entry falls back
// to English (S6.05, "A missing key falls back to the default language").
// Error copy stays in `organization-error-copy.ts`, mapped from a
// `HandledError` code, not from here.

export type OrganizationCopyKey = keyof typeof EN;
export type OrganizationCatalog = typeof EN;

const EN = {
  roleAdmin: "Admin",
  roleMember: "Member",
  roleViewer: "Viewer",
  lastAdminReason: "An organization always needs at least one admin.",
  breadcrumbHome: "Home",
  breadcrumbSettings: "Settings",
  membersTitle: "Members",
  membersDescription: "Everyone who belongs to this organization, their role and status.",
  leaveOrganization: "Leave organization",
  inviteMember: "Invite member",
  removeMemberTitle: "Remove {name}?",
  removeMemberFallbackTitle: "Remove this member?",
  removeMemberDescription: "They will lose access to this organization right away.",
  remove: "Remove",
  leaveOrganizationTitle: "Leave this organization?",
  leaveOrganizationDescription: "You will lose access to it right away. You can be invited back later.",
  leave: "Leave",
  inviteSomeoneTitle: "Invite someone",
  inviteSomeoneDescription: "They will receive an email with a link to join.",
  sendInvite: "Send invite",
  emailAddress: "Email address",
  emailAddressPlaceholder: "person@example.com",
  role: "Role",
  inviteLinkTitle: "Invite link",
  inviteLinkDescription:
    "Anyone with this link can join with the invited role. Resending or copying again replaces it.",
  done: "Done",
  inviteLinkLabel: "Invite link",
  revokeInviteTitle: "Revoke the invite to {email}?",
  revokeInviteFallbackTitle: "Revoke this invite?",
  revokeInviteDescription: "Its link stops working right away.",
  revoke: "Revoke",
  pendingInvitations: "Pending invitations",
  noPendingInvitations: "No pending invitations",
  invited: "Invited",
  status: "Status",
  lastSent: "Last sent",
  actions: "Actions",
  expired: "Expired",
  pending: "Pending",
  resendTooltip: "Resend (renews the link)",
  copyLinkTooltip: "Copy a fresh link",
  revokeTooltip: "Revoke",
  resendInviteAria: "Resend invite to {email}",
  copyInviteLinkAria: "Copy invite link for {email}",
  revokeInviteAria: "Revoke invite to {email}",
  noMembersYet: "This organization has no members yet",
  member: "Member",
  you: "You",
  roleForAria: "Role for {name}",
  disabled: "Disabled",
  active: "Active",
  removeMemberAria: "Remove {name}",
  missingInviteTitle: "This link is missing its invite",
  missingInviteBanner: "There is no invite token in this link.",
  backToSignIn: "Back to Sign in",
  acceptedTitle: "You're in",
  acceptedBannerTitle: "You joined the organization.",
  acceptedBannerDescription: "You are now a {role}.",
  continue: "Continue",
  declinedTitle: "Invite declined",
  declinedBanner: "You declined this invite. Its link no longer works.",
  goToProduct: "Go to {product}",
  invitedTitle: "You've been invited",
  invitedSubtitle: "Accept to join, or decline the invite.",
  joining: "Joining…",
  acceptAndJoin: "Accept and join",
  signUpOrSignInBanner:
    "Sign up or sign in with the address this invite was sent to, then come back to this link.",
  createAccount: "Create an account",
  signIn: "Sign in",
  declining: "Declining…",
  decline: "Decline",

  // The switcher (S1.05): every organization the caller belongs to, with its
  // role, switching and creating one from the shell's top bar.
  cancel: "Cancel",
  switcherLabel: "Switch organization",
  organizations: "Organizations",
  createOrganization: "Create organization",
  manageOrganization: "Organization settings",
  pendingDeletionBadge: "Pending deletion",
  restore: "Restore",
  newOrganizationTitle: "Create an organization",
  newOrganizationNameLabel: "Organization name",
  newOrganizationNamePlaceholder: "Acme Inc",
  create: "Create",
  creating: "Creating…",

  // Organization settings (S1.06): rename and logo. Deleting it is no longer a
  // customer's own action (D307): the one grace-period action left here is
  // undoing a pending deletion, whatever started it.
  settingsTitle: "Organization",
  settingsDescription: "Rename the organization or set its logo.",
  generalPanelTitle: "General",
  organizationNameLabel: "Name",
  organizationNameHint: { one: "Up to 1 character", other: "Up to {count} characters" },
  saveChanges: "Save changes",
  saving: "Saving…",
  logoLabel: "Logo",
  logoHint: "PNG, JPEG or WebP, up to 500KB.",
  uploadLogo: "Upload logo",
  uploading: "Uploading…",
  removeLogo: "Remove",
  pendingDeletionTitle: "{name} is pending deletion",
  pendingDeletionDescription: "It will be purged for good on {date}. Restore it to keep it.",
  restoring: "Restoring…",
  organizationRenamed: "Organization renamed",
  logoUpdated: "Logo updated",
  logoRemoved: "Logo removed",
  organizationRestored: "Organization restored",
  organizationCreated: "Organization created",
  switchedTo: "Switched to {name}",

  // Document titles and navigation.
  screenTitleMembers: "Members · {product}",
  screenTitleSettings: "Organization · {product}",
  screenTitleAcceptInvite: "Accept invite · {product}",
  navMembers: "Members",
  navOrganization: "Organization",

  // Toasts and the reasons a change did not go through.
  roleChanged: "Role changed",
  couldNotChangeRole: "Could not change that role",
  memberRemoved: "Member removed",
  couldNotRemoveMember: "Could not remove that member",
  youLeftOrganization: "You left the organization",
  couldNotLeave: "Could not leave this organization",
  enterValidEmail: "Enter a valid email address.",
  inviteSent: "Invite sent",
  couldNotSendInvite: "Could not send that invite",
  inviteResent: "Invite resent",
  couldNotResendInvite: "Could not resend that invite",
  couldNotCreateInviteLink: "Could not create a link for that invite",
  inviteRevoked: "Invite revoked",
  couldNotRevokeInvite: "Could not revoke that invite",
  couldNotRename: "Could not rename the organization",
  couldNotUploadLogo: "Could not upload that logo",
  couldNotRemoveLogo: "Could not remove the logo",
  couldNotRestore: "Could not restore the organization",
  couldNotRestoreThat: "Could not restore that organization",
  couldNotCreateOrganization: "Could not create the organization",
  inviteLinkFailed: "This invite link did not work",
  nameMaxHint: "Up to {max} characters",
} as const;

const ES: Translation<OrganizationCatalog> = {
  roleAdmin: "Administrador",
  roleMember: "Miembro",
  roleViewer: "Visualizador",
  lastAdminReason: "Una organización siempre necesita al menos un administrador.",
  breadcrumbHome: "Inicio",
  breadcrumbSettings: "Configuración",
  membersTitle: "Miembros",
  membersDescription: "Todos los que pertenecen a esta organización, su rol y su estado.",
  leaveOrganization: "Salir de la organización",
  inviteMember: "Invitar miembro",
  removeMemberTitle: "¿Quitar a {name}?",
  removeMemberFallbackTitle: "¿Quitar a este miembro?",
  removeMemberDescription: "Perderá el acceso a esta organización de inmediato.",
  remove: "Quitar",
  leaveOrganizationTitle: "¿Salir de esta organización?",
  leaveOrganizationDescription: "Perderás el acceso de inmediato. Puedes ser invitado de nuevo más adelante.",
  leave: "Salir",
  inviteSomeoneTitle: "Invitar a alguien",
  inviteSomeoneDescription: "Recibirá un correo con un enlace para unirse.",
  sendInvite: "Enviar invitación",
  emailAddress: "Correo electrónico",
  emailAddressPlaceholder: "persona@ejemplo.com",
  role: "Rol",
  inviteLinkTitle: "Enlace de invitación",
  inviteLinkDescription:
    "Cualquiera con este enlace puede unirse con el rol invitado. Reenviar o copiar de nuevo lo reemplaza.",
  done: "Listo",
  inviteLinkLabel: "Enlace de invitación",
  revokeInviteTitle: "¿Revocar la invitación a {email}?",
  revokeInviteFallbackTitle: "¿Revocar esta invitación?",
  revokeInviteDescription: "Su enlace deja de funcionar de inmediato.",
  revoke: "Revocar",
  pendingInvitations: "Invitaciones pendientes",
  noPendingInvitations: "No hay invitaciones pendientes",
  invited: "Invitado",
  status: "Estado",
  lastSent: "Último envío",
  actions: "Acciones",
  expired: "Vencida",
  pending: "Pendiente",
  resendTooltip: "Reenviar (renueva el enlace)",
  copyLinkTooltip: "Copiar un enlace nuevo",
  revokeTooltip: "Revocar",
  resendInviteAria: "Reenviar invitación a {email}",
  copyInviteLinkAria: "Copiar enlace de invitación para {email}",
  revokeInviteAria: "Revocar invitación a {email}",
  noMembersYet: "Esta organización no tiene miembros todavía",
  member: "Miembro",
  you: "Tú",
  roleForAria: "Rol de {name}",
  disabled: "Deshabilitado",
  active: "Activo",
  removeMemberAria: "Quitar a {name}",
  missingInviteTitle: "A este enlace le falta la invitación",
  missingInviteBanner: "No hay un código de invitación en este enlace.",
  backToSignIn: "Volver a iniciar sesión",
  acceptedTitle: "Ya estás dentro",
  acceptedBannerTitle: "Te uniste a la organización.",
  acceptedBannerDescription: "Ahora eres {role}.",
  continue: "Continuar",
  declinedTitle: "Invitación rechazada",
  declinedBanner: "Rechazaste esta invitación. Su enlace ya no funciona.",
  goToProduct: "Ir a {product}",
  invitedTitle: "Te han invitado",
  invitedSubtitle: "Acepta para unirte, o rechaza la invitación.",
  joining: "Uniéndote…",
  acceptAndJoin: "Aceptar y unirme",
  signUpOrSignInBanner:
    "Regístrate o inicia sesión con la dirección a la que se envió esta invitación, y luego vuelve a este enlace.",
  createAccount: "Crear una cuenta",
  signIn: "Iniciar sesión",
  declining: "Rechazando…",
  decline: "Rechazar",

  cancel: "Cancelar",
  switcherLabel: "Cambiar de organización",
  organizations: "Organizaciones",
  createOrganization: "Crear organización",
  manageOrganization: "Configuración de la organización",
  pendingDeletionBadge: "Eliminación pendiente",
  restore: "Restaurar",
  newOrganizationTitle: "Crear una organización",
  newOrganizationNameLabel: "Nombre de la organización",
  newOrganizationNamePlaceholder: "Acme Inc",
  create: "Crear",
  creating: "Creando…",

  settingsTitle: "Organización",
  settingsDescription: "Renombra la organización o cambia su logo.",
  generalPanelTitle: "General",
  organizationNameLabel: "Nombre",
  organizationNameHint: { one: "Hasta 1 carácter", other: "Hasta {count} caracteres" },
  saveChanges: "Guardar cambios",
  saving: "Guardando…",
  logoLabel: "Logo",
  logoHint: "PNG, JPEG o WebP, hasta 500KB.",
  uploadLogo: "Subir logo",
  uploading: "Subiendo…",
  removeLogo: "Quitar",
  pendingDeletionTitle: "{name} tiene una eliminación pendiente",
  pendingDeletionDescription: "Se eliminará para siempre el {date}. Restáurala para conservarla.",
  restoring: "Restaurando…",
  organizationRenamed: "Organización renombrada",
  logoUpdated: "Logo actualizado",
  logoRemoved: "Logo eliminado",
  organizationRestored: "Organización restaurada",
  organizationCreated: "Organización creada",
  switchedTo: "Cambiaste a {name}",

  screenTitleMembers: "Miembros · {product}",
  screenTitleSettings: "Organización · {product}",
  screenTitleAcceptInvite: "Aceptar invitación · {product}",
  navMembers: "Miembros",
  navOrganization: "Organización",

  roleChanged: "Rol cambiado",
  couldNotChangeRole: "No se pudo cambiar ese rol",
  memberRemoved: "Miembro quitado",
  couldNotRemoveMember: "No se pudo quitar a ese miembro",
  youLeftOrganization: "Saliste de la organización",
  couldNotLeave: "No se pudo salir de esta organización",
  enterValidEmail: "Escribe un correo electrónico válido.",
  inviteSent: "Invitación enviada",
  couldNotSendInvite: "No se pudo enviar esa invitación",
  inviteResent: "Invitación reenviada",
  couldNotResendInvite: "No se pudo reenviar esa invitación",
  couldNotCreateInviteLink: "No se pudo crear un enlace para esa invitación",
  inviteRevoked: "Invitación revocada",
  couldNotRevokeInvite: "No se pudo revocar esa invitación",
  couldNotRename: "No se pudo renombrar la organización",
  couldNotUploadLogo: "No se pudo subir ese logo",
  couldNotRemoveLogo: "No se pudo quitar el logo",
  couldNotRestore: "No se pudo restaurar la organización",
  couldNotRestoreThat: "No se pudo restaurar esa organización",
  couldNotCreateOrganization: "No se pudo crear la organización",
  inviteLinkFailed: "Este enlace de invitación no funcionó",
  nameMaxHint: "Hasta {max} caracteres",
};

export const ORGANIZATION_CATALOGS: Catalogs<OrganizationCatalog> = { en: EN, es: ES };

/** The same three options everywhere a role is picked (S4.06). */
export function roleOptions(
  t: Translate<OrganizationCatalog>,
): ReadonlyArray<{ readonly value: MembershipRole; readonly label: string }> {
  return [
    { value: "admin", label: t("roleAdmin") },
    { value: "member", label: t("roleMember") },
    { value: "viewer", label: t("roleViewer") },
  ];
}

/** The label shown for a member's or an invitation's role, read-only context. */
export function roleLabel(t: Translate<OrganizationCatalog>, role: MembershipRole): string {
  switch (role) {
    case "admin":
      return t("roleAdmin");
    case "member":
      return t("roleMember");
    case "viewer":
      return t("roleViewer");
  }
}

/**
 * Whether `member` is this list's only active admin, so the screen can disable
 * their role picker, remove and (if it is the caller) leave, with a reason,
 * before ever sending the request (ADR-002 §3, S3.03). The server enforces the
 * same rule regardless: this only saves a trip for the one row it can never
 * apply to.
 */
export function isOnlyActiveAdmin(members: readonly MemberView[], userId: string): boolean {
  const activeAdmins = members.filter((member) => member.role === "admin" && !member.disabled);
  return activeAdmins.length === 1 && activeAdmins[0]?.userId === userId;
}

/** A cheap client-side check before sending an invite (S3.04): the server's
 * own `z.email()` is the real rule, this only saves an obviously bad trip. */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}
